import React from 'react';
import type { Service, VehiculeConfig } from '../../../../shared/types/config';

interface VehiculesByServiceEntry {
  service: Service;
  byType: Record<string, VehiculeConfig[]>;
  types: string[];
}

interface VehiculesByServicePickerProps {
  loadingVehicules: boolean;
  vehiculesByService: VehiculesByServiceEntry[];
  vehiculeSearchQuery: string;
  setVehiculeSearchQuery: (v: string) => void;
  openServiceId: number | null;
  setOpenServiceId: (id: number | null) => void;
  vehiculeQuantites: Record<number, number>;
  setVehiculeQuantite: (id: number, qty: number) => void;
}

const VehiculesByServicePicker: React.FC<VehiculesByServicePickerProps> = ({
  loadingVehicules,
  vehiculesByService,
  vehiculeSearchQuery,
  setVehiculeSearchQuery,
  openServiceId,
  setOpenServiceId,
  vehiculeQuantites,
  setVehiculeQuantite,
}) => (
  <div className="md:col-span-2">
    <label className="block text-xs font-medium text-slate-400 mb-1.5">Véhicules par service</label>
    {loadingVehicules ? (
      <p className="text-sm text-slate-500">Chargement des services et véhicules...</p>
    ) : vehiculesByService.length === 0 ? (
      <p className="text-sm text-slate-500">Aucun service ou véhicule configuré.</p>
    ) : (
      <>
        <input
          type="text"
          value={vehiculeSearchQuery}
          onChange={(e) => setVehiculeSearchQuery(e.target.value)}
          placeholder="Rechercher un véhicule ou un type..."
          className="w-full mb-3 rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60"
        />
        <div className="space-y-2 rounded-lg border border-[#2b3a61] overflow-hidden">
          {vehiculesByService.map(({ service, byType, types }) => {
            const searchLower = vehiculeSearchQuery.trim().toLowerCase();
            const filterV = (list: VehiculeConfig[]) =>
              !searchLower
                ? list
                : list.filter(
                    (v) =>
                      v.nom.toLowerCase().includes(searchLower) ||
                      (v.type || '').toLowerCase().includes(searchLower)
                  );
            const typesFiltered = types.filter((t) => filterV(byType[t] || []).length > 0);
            const count = typesFiltered.reduce((acc, t) => acc + filterV(byType[t] || []).length, 0);
            if (count === 0) return null;
            const isOpen = openServiceId === service.id;
            return (
              <div key={service.id} className="bg-[#0d1b3a]/80">
                <button
                  type="button"
                  onClick={() => setOpenServiceId(isOpen ? null : service.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left text-slate-200 hover:bg-[#1e2d4d]/50 transition-colors"
                >
                  <span className="font-medium text-sm">{service.nom}</span>
                  <span className="text-xs text-slate-400">
                    {count} véhicule{count !== 1 ? 's' : ''}
                  </span>
                  <svg
                    className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="border-t border-[#1e2d4d] px-4 py-3 space-y-4">
                    {typesFiltered.map((type) => {
                      const list = filterV(byType[type] || []);
                      return (
                        <div key={type}>
                          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                            {type}
                          </div>
                          <ul className="space-y-2">
                            {list.map((v, i) => (
                              <li key={v.id ?? i} className="flex items-center justify-between gap-3 text-sm text-slate-200">
                                <span className="min-w-0 truncate">{v.nom}</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={vehiculeQuantites[v.id ?? 0] ?? 0}
                                  onChange={(e) =>
                                    setVehiculeQuantite(v.id ?? 0, parseInt(e.target.value, 10) || 0)
                                  }
                                  className="w-20 rounded border border-[#2a3a62] bg-[#0a132b] px-2 py-1 text-sm text-slate-100 focus:outline-none shrink-0"
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-1">Quantité requise pour cette intervention (0 par défaut).</p>
      </>
    )}
  </div>
);

export default VehiculesByServicePicker;
